import { Fragment, useEffect, useMemo, useRef, useState } from "react";

import type { ComboboxInputVariant, ComboboxLabelValue } from "@/components/ui/combobox";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxSeparator,
} from "@/components/ui/combobox";

export type ContainerComboboxBaseItem = ComboboxLabelValue;

export type ContainerComboboxProps<TItem extends ContainerComboboxBaseItem> = {
  items: TItem[];
  selectedValue: TItem | null;
  isLoading?: boolean;

  placeholder?: string;
  emptyText?: string;

  /**
   * Called when the user selects an item from the list.
   * NOTE: the component will blur the input after selection.
   */
  onSelect: (item: TItem) => void;

  /**
   * Customize item equality when `value` objects may not be referentially stable.
   * Defaults to `item.value === selected.value`.
   */
  isItemEqualToValue?: (item: TItem, selected: TItem) => boolean;

  /**
   * Customize item rendering in the list. If not provided, defaults to a basic
   * `ComboboxItem` rendering.
   *
   * You can return a `Fragment` containing separators, custom markup, etc.
   */
  renderItem?: (item: TItem) => React.ReactNode;

  /**
   * If provided, controls the subtle input's max width behavior.
   * Passed through to `ComboboxInput`.
   */
  shrinkToFit?: number;

  variant?: ComboboxInputVariant;
};

export function ContainerCombobox<TItem extends ContainerComboboxBaseItem>({
  items,
  selectedValue,
  isLoading = false,
  placeholder = "Select an option",
  emptyText = "No results found",
  onSelect,
  isItemEqualToValue = (item, selected) => item.value === selected.value,
  renderItem,
  shrinkToFit,
  variant = "subtle",
}: ContainerComboboxProps<TItem>) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [inputValue, setInputValue] = useState("");
  const [value, setValue] = useState<TItem | null>(null);

  useEffect(() => {
    setValue(selectedValue);
    setInputValue(selectedValue?.label ?? "");
  }, [selectedValue]);

  const handleBlur = () => {
    // If the user clears the input and clicks away, restore the selected label
    // so we don't end up with an empty input while a value is selected.
    if (!inputValue.trim() && selectedValue) {
      setValue(selectedValue);
      setInputValue(selectedValue.label);
    }
  };

  const defaultRenderItem = useMemo(() => {
    return (item: TItem) => (
      <ComboboxItem key={item.value} value={item}>
        {item.label}
      </ComboboxItem>
    );
  }, []);

  const render = renderItem ?? defaultRenderItem;

  return (
    <Combobox<TItem>
      items={items}
      value={value}
      inputValue={inputValue}
      onValueChange={(next) => {
        setValue(next);
        inputRef.current?.blur();
        if (next) onSelect(next);
      }}
      onInputValueChange={(nextInputValue) => {
        setInputValue(nextInputValue);
      }}
      isItemEqualToValue={isItemEqualToValue}
    >
      <ComboboxInput
        ref={inputRef}
        placeholder={isLoading ? "" : placeholder}
        onBlur={handleBlur}
        onMouseUp={(e) => {
          e.currentTarget.select();
        }}
        variant={variant}
        shrinkToFit={shrinkToFit}
      />

      <ComboboxContent maxWidth>
        <ComboboxEmpty className="px-2">{emptyText}</ComboboxEmpty>
        <ComboboxList>
          {(item) => <Fragment key={item.value}>{render(item)}</Fragment>}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

export function renderContainerComboboxItemWithOptionalSeparator<
  TItem extends ContainerComboboxBaseItem,
>({
  item,
  shouldRenderSeparatorAfter,
}: {
  item: TItem;
  shouldRenderSeparatorAfter: boolean;
}) {
  return (
    <Fragment key={item.value}>
      <ComboboxItem value={item}>{item.label}</ComboboxItem>
      {!!shouldRenderSeparatorAfter && <ComboboxSeparator />}
    </Fragment>
  );
}
